function initShopifyConsentTracking() {
  if (!window.Shopify || !window.Shopify.loadFeatures) {
    window.setTimeout(initShopifyConsentTracking, 100);
    return;
  }

  window.Shopify.loadFeatures(
    [
      {
        name: "consent-tracking-api",
        version: "0.1",
      },
    ],
    () => {},
  );
}

class CustomCookieConsent extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });

    this.allowNativeDialog = false;
    this.isVisible = false;
    this.handleAcceptAll = this.handleAcceptAll.bind(this);
    this.handleCustomize = this.handleCustomize.bind(this);
    this.hideCustomDialog = this.hideCustomDialog.bind(this);

    this.installNativeDialogStyles();

    // Only stand in for Cookiebot's first-visit banner. Once consent exists, leave Cookiebot alone
    // so its renewal widget and dialog behave normally.
    if (this.hasStoredConsent()) this.releaseNativeDialog();

    let readyFired = false;
    window.addEventListener("CookiebotOnConsentReady", () => {
      readyFired = true;
      this.checkCookiebotState();
      this.syncConsentToShopify();
    });
    window.addEventListener("CookiebotOnAccept", () => {
      this.releaseNativeDialog();
      this.hideCustomDialog();
      this.syncConsentToShopify();
    });
    window.addEventListener("CookiebotOnDecline", () => {
      this.releaseNativeDialog();
      this.hideCustomDialog();
      this.syncConsentToShopify();
    });
    window.addEventListener("CookiebotOnDialogDisplay", () => {
      if (this.allowNativeDialog) return;
      if (window.Cookiebot && typeof window.Cookiebot.hide === "function") {
        window.Cookiebot.hide();
      }
      this.checkCookiebotState();
    });

    window.setTimeout(() => {
      if (!readyFired) {
        this.checkCookiebotState();
        this.syncConsentToShopify();
      }
    }, 2000);
  }

  connectedCallback() {
    this.render();
  }

  render() {
    if (this.shadowRoot.hasChildNodes()) return;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --dialog-bg: #fff;
          --dialog-text: #333;
          --button-primary-bg: #000;
          --button-primary-text: #fff;
          --button-secondary-bg: #e5e5e5;
          --button-secondary-text: #333;
          --font-family: system-ui, -apple-system, sans-serif;
        }

        .cookie-dialog {
          position: fixed;
          z-index: 999999;
          visibility: hidden;
          width: calc(100vw - 32px);
          max-width: 400px;
          box-sizing: border-box;
          padding: 16px;
          color: var(--dialog-text);
          background: var(--dialog-bg);
          border-radius: 6px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
          font-family: var(--font-family);
          font-size: 14px;
          line-height: 1.5;
          opacity: 0;
          pointer-events: none;
          transform: translateY(30px);
          /* Lift above the sticky ATC bar with \`translate\` (composited, stacks with \`transform\`) rather than \`bottom\`,
             so it stays in step with the bar. --sticky-atc-bar-height is set on <html> by product.js while the bar is visible. */
          translate: 0 calc(var(--sticky-atc-bar-height, 0px) * -1);
          transition: opacity 0.4s ease-in-out, transform 0.4s ease-in-out, translate 0.5s cubic-bezier(0, 0, 0.2, 1);
        }

        .cookie-dialog.visible {
          visibility: visible;
          opacity: 1;
          pointer-events: auto;
          transform: translateY(0);
        }

        .dialog-content {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 24px;
        }

        .dialog-text {
          flex: 1;
          margin: 0;
          color: var(--dialog-text);
          font-weight: 400;
          line-height: 1.5;
        }

        .button-group {
          display: flex;
          flex-direction: row;
          gap: 12px;
        }

        .btn {
          width: 100%;
          box-sizing: border-box;
          padding: 14px 20px;
          border: 0;
          border-radius: 0;
          font-family: var(--font-family);
          font-size: 14px;
          font-weight: 400;
          text-align: center;
          text-decoration: none;
          white-space: nowrap;
          cursor: pointer;
          transition: opacity 0.2s ease, background-color 0.2s ease;
        }

        .btn-primary {
          color: var(--button-primary-text);
          background: var(--button-primary-bg);
        }

        .btn-primary:hover { opacity: 0.9; }

        .btn-secondary {
          color: var(--button-secondary-text);
          background: var(--button-secondary-bg);
        }

        .btn-secondary:hover { background: #d5d5d5; }

        .close-btn {
          display: flex;
          flex-shrink: 0;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          padding: 4px;
          color: var(--dialog-text);
          background: none;
          border: 0;
          border-radius: 50%;
          font-size: 20px;
          opacity: 0.6;
          cursor: pointer;
        }

        .close-btn:hover {
          background: rgba(0, 0, 0, 0.1);
          opacity: 1;
        }

        @media (max-width: 767px) {
          .cookie-dialog {
            bottom: 20px;
            left: 50%;
            max-width: 350px;
            transform: translateX(-50%) translateY(30px);
          }

          .cookie-dialog.visible { transform: translateX(-50%) translateY(0); }
        }

        @media (min-width: 768px) {
          .cookie-dialog {
            right: 24px;
            bottom: 24px;
            width: 380px;
            max-width: 380px;
          }

          .btn { flex: 1; }
        }
      </style>

      <div class="cookie-dialog" id="cookieDialog">
        <div class="dialog-content">
          <p class="dialog-text">
            We use cookies to personalize content, ads, and social media features, and to analyze traffic. We may share this data with our social media, advertising, and analytics partners.
          </p>
          <button class="close-btn" id="closeBtn" type="button" aria-label="Close" title="Close">×</button>
        </div>
        <div class="button-group">
          <button class="btn btn-secondary" id="customizeBtn" type="button">CUSTOMIZE</button>
          <button class="btn btn-primary" id="allowAllBtn" type="button">ALLOW ALL</button>
        </div>
      </div>
    `;

    this.shadowRoot
      .getElementById("allowAllBtn")
      .addEventListener("click", this.handleAcceptAll);
    this.shadowRoot
      .getElementById("customizeBtn")
      .addEventListener("click", this.handleCustomize);
    this.shadowRoot
      .getElementById("closeBtn")
      .addEventListener("click", this.hideCustomDialog);
  }

  installNativeDialogStyles() {
    if (document.getElementById("custom-cookiebot-brand-styles")) return;

    const style = document.createElement("style");
    style.id = "custom-cookiebot-brand-styles";
    style.textContent = `
      #CybotCookiebotDialog {
        --cb-color-button-background: #000;
        --cb-color-button-text: #fff;
        --cb-color-button-secondary-background: #fff;
        --cb-color-button-secondary-text: #000;
        --cb-color-button-secondary-border: #000;
        --cb-color-accent: #000;
        --cb-color-link: #000;
      }

      #CybotCookiebotDialogBodyLevelButtonLevelOptinAllowAll,
      #CybotCookiebotDialogBodyButtonAccept,
      #CybotCookiebotDialogBodyLevelButtonAccept {
        color: #fff !important;
        background: #000 !important;
        border-color: #000 !important;
      }

      #CybotCookiebotDialogBodyLevelButtonLevelOptinAllowAll:hover,
      #CybotCookiebotDialogBodyButtonAccept:hover,
      #CybotCookiebotDialogBodyLevelButtonAccept:hover {
        color: #fff !important;
        background: #222 !important;
        border-color: #222 !important;
      }

      #CybotCookiebotDialogBodyLevelButtonLevelOptinAllowallSelection,
      #CybotCookiebotDialogBodyButtonDecline,
      #CybotCookiebotDialogBodyLevelButtonLevelOptinDeclineAll {
        color: #000 !important;
        background: #fff !important;
        border-color: #000 !important;
      }

      #CybotCookiebotDialogBodyLevelButtonLevelOptinAllowallSelection:hover,
      #CybotCookiebotDialogBodyButtonDecline:hover,
      #CybotCookiebotDialogBodyLevelButtonLevelOptinDeclineAll:hover {
        color: #000 !important;
        background: #f5f5f5 !important;
        border-color: #000 !important;
      }

      #CybotCookiebotDialog .CybotCookiebotDialogBodyLevelButtonSlider { background: #c4c4c4 !important; }
      #CybotCookiebotDialog input:checked + .CybotCookiebotDialogBodyLevelButtonSlider { background: #000 !important; }

      #CybotCookiebotDialogNav .CybotCookiebotDialogNavItemWrapper.CybotCookiebotDialogActive .CybotCookiebotDialogNavItemLink,
      #CybotCookiebotDialogNav .CybotCookiebotDialogNavItemLink[aria-selected='true'],
      #CybotCookiebotDialog .CybotCookiebotDialogNavItemLink.CybotCookiebotDialogActive {
        color: #000 !important;
        border-color: #000 !important;
      }

      #CybotCookiebotDialogNav .CybotCookiebotDialogNavItemWrapper.CybotCookiebotDialogActive,
      #CybotCookiebotDialogNav .CybotCookiebotDialogNavItemWrapper[aria-selected='true'] {
        border-color: #000 !important;
      }

      #CybotCookiebotDialog a,
      #CybotCookiebotDialog a:visited { color: #000 !important; }
      #CybotCookiebotDialog a:hover { color: #333 !important; }
    `;
    document.head.appendChild(style);
  }

  hasStoredConsent() {
    if (window.Cookiebot && window.Cookiebot.hasResponse) return true;

    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("CookieConsent="));

    if (!cookie) return false;

    const value = decodeURIComponent(cookie.slice("CookieConsent=".length));
    return (
      value.includes("stamp:") &&
      !value.includes("stamp:'-1'") &&
      value !== "-1"
    );
  }

  checkCookiebotState() {
    if (!window.Cookiebot) {
      if (this.hasStoredConsent()) {
        this.hideCustomDialog();
        return;
      }
      window.setTimeout(() => this.checkCookiebotState(), 100);
      return;
    }

    if (this.hasStoredConsent()) {
      this.hideCustomDialog();
      return;
    }

    this.showCustomDialog();
  }

  showCustomDialog() {
    const dialog = this.shadowRoot.getElementById("cookieDialog");
    if (!dialog) return;

    const ccpaApplies = Boolean(
      window.Cookiebot &&
      window.Cookiebot.regulations &&
      window.Cookiebot.regulations.ccpaApplies,
    );
    const closeButton = this.shadowRoot.getElementById("closeBtn");
    if (closeButton) closeButton.style.display = ccpaApplies ? "none" : "";

    dialog.classList.add("visible");
    this.isVisible = true;
  }

  hideCustomDialog() {
    const dialog =
      this.shadowRoot && this.shadowRoot.getElementById("cookieDialog");
    if (!dialog) return;
    dialog.classList.remove("visible");
    this.isVisible = false;
  }

  syncConsentToShopify() {
    if (!window.Cookiebot || !window.Cookiebot.consent) return;

    const consent = window.Cookiebot.consent;
    const shopifyConsentPoll = window.setInterval(() => {
      if (!window.Shopify || !window.Shopify.customerPrivacy) return;

      window.clearInterval(shopifyConsentPoll);
      window.Shopify.customerPrivacy.setTrackingConsent(
        {
          analytics: consent.statistics,
          marketing: consent.marketing,
          preferences: consent.preferences,
          sale_of_data: consent.marketing,
        },
        () => {},
      );
    }, 100);

    if (window.Cookiebot.hasResponse) this.syncConsentToShoplift(consent);
  }

  // Shoplift needs third-party consent tools to report analytics consent explicitly.
  // Only sent after a real banner response, so visitors who never answer keep Shoplift's default.
  // Shoplift loads from its own head snippet; stop waiting after 10s if it isn't on the page.
  syncConsentToShoplift(consent) {
    let attempts = 0;
    const shopliftConsentPoll = window.setInterval(() => {
      attempts += 1;
      const shoplift = window.shoplift;
      if (!shoplift || typeof shoplift.setAnalyticsConsent !== "function") {
        if (attempts >= 100) window.clearInterval(shopliftConsentPoll);
        return;
      }

      window.clearInterval(shopliftConsentPoll);
      shoplift.setAnalyticsConsent(Boolean(consent.statistics));
    }, 100);
  }

  handleAcceptAll() {
    if (window.Cookiebot) {
      ["Preferences", "Statistics", "Marketing"].forEach((name) => {
        const checkbox = document.getElementById(
          `CybotCookiebotDialogBodyLevelButton${name}`,
        );
        if (!checkbox) return;
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      });

      if (
        window.Cookiebot.dialog &&
        typeof window.Cookiebot.dialog.submitConsent === "function"
      ) {
        window.Cookiebot.dialog.submitConsent();
      } else if (typeof window.Cookiebot.submitCustomConsent === "function") {
        window.Cookiebot.submitCustomConsent(true, true, true);
      } else {
        const allowAllButton =
          document.getElementById(
            "CybotCookiebotDialogBodyLevelButtonLevelOptinAllowAll",
          ) || document.getElementById("CybotCookiebotDialogBodyButtonAccept");
        if (allowAllButton) allowAllButton.click();
      }
    }

    this.hideCustomDialog();
    window.setTimeout(() => this.syncConsentToShopify(), 200);
  }

  releaseNativeDialog() {
    this.allowNativeDialog = true;
    const hideStyles = document.getElementById("hide-cookiebot-styles");
    if (hideStyles) hideStyles.remove();
  }

  handleCustomize() {
    this.releaseNativeDialog();
    this.hideCustomDialog();
    if (!window.Cookiebot) return;

    // The native dialog was hidden via Cookiebot.hide(), so re-show it with renew().
    // renew() opens the details view itself only in "inlineoptin" mode; otherwise click Customize once visible.
    window.addEventListener(
      "CookiebotOnDialogDisplay",
      () => {
        const dialog = window.CookieConsentDialog;
        if (dialog && dialog.responseMode === "inlineoptin") return;
        const customizeButton = document.getElementById(
          "CybotCookiebotDialogBodyLevelButtonCustomize",
        );
        if (customizeButton) customizeButton.click();
      },
      { once: true },
    );
    window.Cookiebot.renew();
  }

  show() {
    this.showCustomDialog();
  }

  hide() {
    this.hideCustomDialog();
  }
}

initShopifyConsentTracking();

if (!customElements.get("custom-cookie-consent")) {
  customElements.define("custom-cookie-consent", CustomCookieConsent);
}
